import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, hasSession, refreshSession, setAccessToken } from '../lib/api';

export interface CodeBoxUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  plan: 'free' | 'pro';
  flags: Record<string, unknown>;
}

export interface Capabilities {
  viewCode: boolean;
  editCode: boolean;
  pinModelTier: boolean;
  architectPass: boolean;
  buildBudget: number;
  runBudget: number;
}

interface AuthState {
  user: CodeBoxUser | null;
  capabilities: Capabilities | null;
  experimental: boolean;
  setExperimental: (on: boolean) => void;
  status: 'loading' | 'authenticated' | 'anonymous';
  signOut: () => void;
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CodeBoxUser | null>(null);
  const [capabilities, setCapabilities] = useState<Capabilities | null>(null);
  const [experimental, setExperimental] = useState(false);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const load = useCallback(async () => {
    // OAuth callback (D7 path b) hands the token back in the URL fragment — fragments never reach a
    // server or an access log. Move it into memory and scrub the URL immediately (§10.1).
    let hasToken = false;
    if (window.location.hash.startsWith('#access_token=')) {
      const token = decodeURIComponent(window.location.hash.slice('#access_token='.length));
      if (token) {
        setAccessToken(token);
        hasToken = true;
      }
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    // Default path: cookie SSO — silent refresh against identity-api. Skip it when a token is
    // already in memory (StrictMode runs effects twice; the second pass must not disturb it).
    const refreshed = hasToken || hasSession() || (await refreshSession());
    if (!refreshed) {
      setUser(null);
      setStatus('anonymous');
      return;
    }
    try {
      const me = await api.get<{
        user: CodeBoxUser;
        capabilities: Capabilities;
        experimental: boolean;
      }>('/box/me');
      setUser(me.user);
      setCapabilities(me.capabilities);
      setExperimental(me.experimental);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const signOut = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, capabilities, experimental, setExperimental, status, signOut, reload: load }),
    [user, capabilities, experimental, status, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
